import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Popover,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { useMutation } from '@tanstack/react-query';
import fdbService, { type FdbDurScreenResponse } from '../../../../../services/fdbService';
import { apiErrorMessage } from '../../../../../utils/apiErrorMessage';

/** The two fields of a prescriptions row this reads. */
export interface InteractionRow {
  RxNum: number | string;
  DIN?: string | null;
  DrgDIN?: string | null;
}

/** A DIN as digits without leading zeros, so '09900801' and '9900801' are one drug. */
const dinKey = (din: string | null | undefined): string | null => {
  const digits = String(din ?? '').replace(/\D/g, '').replace(/^0+/, '');
  return digits || null;
};

/** One finding of the screen, as the popover lists it. */
export interface InteractionItem {
  key: string;
  title: string;
  detail: string | null;
  /** At least one of its drugs is a row of this list. */
  onList: boolean;
  dins: string[];
  reasonFor: (din: string) => string;
}

function interactionItems(result: FdbDurScreenResponse): InteractionItem[] {
  const items: InteractionItem[] = [];
  (result.drugDrugInteractions ?? []).forEach((ddi, i) => {
    const severity = ddi.severity?.description || null;
    const name1 = ddi.drug1?.name || ddi.drug1?.din || 'Unknown drug';
    const name2 = ddi.drug2?.name || ddi.drug2?.din || 'Unknown drug';
    const din1 = dinKey(ddi.drug1?.din);
    const din2 = dinKey(ddi.drug2?.din);
    const suffix = severity ? ` (${severity})` : '';
    items.push({
      key: `ddi-${i}`,
      title: `${name1} and ${name2}${severity ? `: ${severity}` : ''}`,
      detail: ddi.description || null,
      onList: false,
      dins: [din1, din2].filter(Boolean) as string[],
      reasonFor: (din) => `Interacts with ${din === din1 ? name2 : name1}${suffix}`,
    });
  });
  (result.duplicateTherapies ?? []).forEach((dup, i) => {
    items.push({
      key: `dup-${i}`,
      title: `Duplicate therapy: ${dup.therapyClass}`,
      detail: (dup.drugs ?? []).map((d) => d.name || d.din).join(', ') || null,
      onList: false,
      dins: (dup.drugs ?? []).map((d) => dinKey(d.din)).filter(Boolean) as string[],
      reasonFor: () => `Duplicate therapy: ${dup.therapyClass}`,
    });
  });
  return items;
}

/**
 * The DIN each row stands for. When the screen says which Rx numbers it read
 * (`medications[]`), only those rows take part, with the DIN the screen read
 * (review L8). A row the screen did not read is never claimed. Without that
 * list, the row's own DIN is used.
 */
function rowDins(result: FdbDurScreenResponse, rows: InteractionRow[]): Map<string, string> {
  const dinByRx = new Map<string, string>();
  for (const med of result.medications ?? []) {
    const key = dinKey(med.din);
    if (key && med.rxNumber != null) dinByRx.set(String(med.rxNumber), key);
  }
  const out = new Map<string, string>();
  for (const row of rows) {
    const rx = String(row.RxNum);
    const din = dinByRx.size > 0 ? dinByRx.get(rx) : dinKey(row.DIN ?? row.DrgDIN);
    if (din) out.set(rx, din);
  }
  return out;
}

/** The screen read against this list: the flags per row, and each finding. */
export function interactionSummary(result: FdbDurScreenResponse, rows: InteractionRow[]) {
  const dinsByRx = rowDins(result, rows);
  const listedDins = new Set(dinsByRx.values());
  const items = interactionItems(result).map((item) => ({
    ...item,
    onList: item.dins.some((din) => listedDins.has(din)),
  }));
  const flags = new Map<string, string[]>();
  for (const [rx, din] of dinsByRx) {
    const reasons: string[] = [];
    for (const item of items) {
      if (!item.dins.includes(din)) continue;
      const reason = item.reasonFor(din);
      if (!reasons.includes(reason)) reasons.push(reason);
    }
    if (reasons.length > 0) flags.set(rx, reasons);
  }
  return { flags, items };
}

/**
 * PRX-137: which rows the FDB patient screen involves, and why. Drug-drug
 * interactions name the other drug and the severity; duplicate therapy names
 * the class.
 */
export function interactionFlagsByRx(result: FdbDurScreenResponse, rows: InteractionRow[]): Map<string, string[]> {
  return interactionSummary(result, rows).flags;
}

export interface RxInteractionsChipProps {
  /** The patient's own pharmacy (the screen reads that PMS). */
  pharmacyId: string;
  /** The composite patient id. */
  patientId: string;
  rows: InteractionRow[];
  /** The flagged rows after a screen, or null when there is no current result. */
  onFlags: (flags: Map<string, string[]> | null) => void;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * PRX-137: "Check interactions" runs the existing FDB patient screen
 * (`POST /api/fdb/:pharmacyId/dur/screen-patient/:patientId`) only when asked. Its
 * medication read covers Kroll and Fillware.
 * - A failure stays on screen as a failure.
 * - A screen that read no medications says what the server said, never
 *   "no interactions".
 * - A result opens a list of every finding (review M4), including those whose
 *   drugs are not rows of this list.
 */
export const RxInteractionsChip: React.FC<RxInteractionsChipProps> = ({ pharmacyId, patientId, rows, onFlags }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const screen = useMutation({
    mutationFn: () => fdbService.screenPatientDur(pharmacyId, patientId),
    onSuccess: (result) => onFlags(result.drugCount > 0 ? interactionSummary(result, rows).flags : null),
    onError: () => onFlags(null),
  });

  const run = () => {
    setAnchor(null);
    if (!screen.isPending) screen.mutate();
  };

  if (screen.isPending) {
    return <Chip size="small" icon={<CircularProgress size={14} />} label="Checking interactions" disabled />;
  }

  if (screen.isError) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip size="small" color="error" variant="outlined" label="Couldn't check interactions. Retry" onClick={run} />
        <Typography role="status" variant="caption" color="error">
          {apiErrorMessage(screen.error, 'The interaction check failed.')}
        </Typography>
      </Box>
    );
  }

  const result = screen.data;
  if (!result) {
    return (
      <Chip
        size="small"
        variant="outlined"
        icon={<FactCheckOutlinedIcon fontSize="small" />}
        label="Check interactions"
        onClick={run}
      />
    );
  }

  if (result.drugCount === 0) {
    return (
      <Chip size="small" variant="outlined" label={`Not checked: ${result.message || 'no medications to screen'}`} onClick={run} />
    );
  }

  const { items } = interactionSummary(result, rows);
  const found = (result.interactionCount ?? 0) + (result.duplicateTherapyCount ?? 0);
  const notOnList = items.filter((item) => !item.onList).length;
  const unresolved = result.unresolvedDins?.length ?? 0;
  let label = found > 0 ? `${plural(found, 'interaction')} found` : 'No interactions found';
  if (found > 0 && notOnList > 0) label += ` (${notOnList} not on this list)`;
  if (unresolved > 0) label += `, ${plural(unresolved, 'drug')} not in FDB`;

  return (
    <>
      <Chip
        size="small"
        color={found > 0 ? 'warning' : 'success'}
        variant={found > 0 ? 'filled' : 'outlined'}
        icon={found > 0 ? <WarningAmberIcon fontSize="small" /> : undefined}
        label={label}
        onClick={(e) => (found > 0 ? setAnchor(e.currentTarget) : run())}
        aria-haspopup={found > 0 ? 'dialog' : undefined}
      />
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { p: 1.5, maxWidth: 420 },
            ...({ role: 'dialog', 'aria-label': 'Interactions found' } as React.HTMLAttributes<HTMLDivElement>),
          },
        }}
      >
        <Typography variant="subtitle2">Interactions found</Typography>
        <List dense disablePadding>
          {items.map((item) => (
            <ListItem key={item.key} disableGutters alignItems="flex-start">
              <ListItemText
                primary={item.title}
                secondary={[item.detail, item.onList ? null : 'Not on this list'].filter(Boolean).join(' · ') || null}
              />
            </ListItem>
          ))}
        </List>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Checked against FDB.
          </Typography>
          <Button size="small" onClick={run}>
            Check again
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default RxInteractionsChip;
